<template>
  <div class="chat-body">
    <progress-bar />
    <template v-if="isWsReady">
      <div class="chat-scroll-body" :class="{'open-chat': isShowingOpenChat}" @scroll.passive="handleScroll()">
        <div
          v-for="(group, groupIndex) in messageGroups"
          :key="groupIndex"
          class="message-group"
        >
          <message-item
            v-for="(msg, index) in group"
            :key="index"
            :data-message-id="msg.id"
            :data-message-payload="msg.payload || msg.id"
            :message="msg"
            :message-group="group"
            :is-last-message-group="groupIndex === messageGroups.length - 1"
          />
        </div>
        <div v-if="!messages.autoScroll && messages.unreadMessagesCount > 0" class="scroll-down-container">
          <span class="notification-counter">{{ messages.unreadMessagesCount }}</span>
          <a
            class="scroll-down-button"
            tabindex="0"
            :aria-label="$t('chatbot.scrollToTheLatestMessage')"
            @click="scrollDown"
            @keydown.enter="scrollDown"
            @keydown.space.prevent="scrollDown"
          />
        </div>
        <!-- for layout purposes we need a message group even without a message -->
        <div v-if="messageGroups.length === 0" class="message-group" />
        <typing-indicator v-if="!isShowingOpenChat" />
        <div v-if="isLastMessageStreamed" class="auto-scroll-anchor" />
      </div>
    </template>
    <template v-else>
      <loading />
    </template>
  </div>
</template>

<script>
// @flow

import { mapGetters, mapState } from 'vuex'

import TypingIndicator from '../TypingIndicator/TypingIndicator.vue'
import ProgressBar from '../ProgressBar/ProgressBar.vue'
import Loading from '../Loading/Loading.vue'
import MessageItem from '../MessageItem/MessageItem.vue'

import { scrollToFirstNewMessageOrTypingIndicator, scrollToLastMessage, scrollLimit } from '../../../common/scrolling'
import { messagesGroupedByReceiver } from '../../../common/messages'
import { isTypingMessage } from '../../models/Message'

export default {
  components: {
    MessageItem,
    Loading,
    TypingIndicator,
    ProgressBar
  },
  data (): Object {
    return {
      firstTimeReceivingMessages: true
    }
  },
  computed: {
    ...mapState(['messages', 'isWsReady', 'bubble', 'customerId']),
    ...mapGetters(['isShowingOpenChat']),
    displayableMessages (): Array<Object> {
      return this.messages.messages.filter(msg => !isTypingMessage(msg))
    },
    isLastMessageStreamed (): any {
      return this.displayableMessages[this.displayableMessages.length - 1]?.streaming
    },
    messageGroups (): any {
      return messagesGroupedByReceiver(this.displayableMessages)
    },
    areAllMessagesFromBotNew (): boolean {
      return this.messages.messages.filter(m => m.from === this.customerId).every(m => m.new)
    }
  },
  watch: {
    'messages.messages' (): void {
      if (this.bubble.isOpenInitial && this.firstTimeReceivingMessages) {
        this.firstTimeReceivingMessages = false
        this.$nextTick(scrollToLastMessage)
      } else if (this.messages.autoScroll) {
        if (!this.areAllMessagesFromBotNew) this.$nextTick(scrollToFirstNewMessageOrTypingIndicator)
        if (this.messages.messages[this.messages.messages.length - 1]?.type === 'products') {
          this.$store.commit('disableAutoScroll')
        }
      }
    },
    'isWsReady' (): void {
      if (this.isWsReady) this.$nextTick(scrollToLastMessage)
    }
  },
  methods: {
    handleScroll (): void {
      const chatBody = document.querySelector('.chat-scroll-body')
      if (chatBody.scrollTop >= scrollLimit(chatBody)) {
        this.$store.commit('enableAutoScroll')
      }
    },
    scrollDown (): void {
      scrollToFirstNewMessageOrTypingIndicator()
      // this should be automatically called by above code, making sure it's done independent of browser functionality
      this.$store.commit('enableAutoScroll')
    }
  }
}
</script>

<style src="./chat-body.scss" />
